import { useReport } from '@ltvco/refresh-lib/hooks';
import { Box } from '@ltvco/refresh-lib/theme';
import { ReportGeneratingScreen } from '@ltvco/refresh-lib/v2';
import { useQuery } from '@ltvco/refresh-lib/vendors';
import { useState } from 'react';

import { adaptCourtOpinionReport } from 'routes/Dashboard/CourtCases/v2/adapters/adaptCourtOpinionReport';
import { JudgeDetails } from 'routes/Dashboard/CourtCases/v2/JudgeSearch';
import { OpinionDetailsHeader } from 'routes/Dashboard/CourtCases/v2/OpinionsSearch/OpinionDetails/OpinionDetailsHeader';
import { OpinionsList } from 'routes/Dashboard/CourtCases/v2/OpinionsSearch/OpinionDetails/OpinionsList';
import { SimilarCases } from 'routes/Dashboard/CourtCases/v2/OpinionsSearch/OpinionDetails/SimilarCases';
import {
  CourtCaseDisclaimer,
  InlineLoading,
  ErrorStateContainer,
  StyledTabsNavigation,
  StyledTab,
  DEFAULT_QUERY_OPTIONS,
  useBaseCourtApi,
} from 'routes/Dashboard/CourtCases/v2/shared';

import type { ReportRouteProps } from './types';
import type { OpinionsResponse } from 'routes/Dashboard/CourtCases/v2/types';

interface CourtOpinionReportProps extends ReportRouteProps {}

function hasPresentCluster(
  cluster: Record<string, unknown> | null | undefined
): cluster is Record<string, unknown> {
  return Boolean(cluster && Object.keys(cluster).length > 0);
}

export function CourtOpinionReportPage({
  permalink,
  firstReportScreen,
}: CourtOpinionReportProps) {
  const [activeTab, setActiveTab] = useState(0);
  const queryResult = useReport(permalink);
  const { searchCaseLaw, backend, canFetchLegal } = useBaseCourtApi();

  const entity = queryResult.data?.entities?.court_opinion;
  const clusterRecord = entity?.cluster;
  const canAdapt = hasPresentCluster(clusterRecord);
  // Only adapt after cluster is present — empty cluster would crash headmatter synthesis.
  const viewModel = entity && canAdapt ? adaptCourtOpinionReport(entity) : null;
  const caseName =
    viewModel?.cluster.case_name_short || viewModel?.cluster.case_name || '';

  const similarCasesQuery = useQuery<OpinionsResponse | null>({
    queryKey: ['similar-cases', backend, caseName],
    queryFn: async () => {
      if (!caseName.trim()) {
        return null;
      }
      const query = `"${caseName.trim()}"`;
      const results = await searchCaseLaw(query, 'score desc');
      return {
        ...results,
        results: results.results.slice(0, 5),
      };
    },
    ...DEFAULT_QUERY_OPTIONS,
    enabled: Boolean(viewModel) && canFetchLegal && Boolean(caseName.trim()),
  });

  const onTabChanged = (newTab: number) => {
    setActiveTab(newTab);
  };

  if (queryResult.isError) {
    return (
      <ErrorStateContainer>
        <p>Court opinion report data is unavailable.</p>
      </ErrorStateContainer>
    );
  }

  if (firstReportScreen?.isActive) {
    return (
      <ReportGeneratingScreen
        reportType="court-opinion"
        ready={!queryResult.isLoading && Boolean(queryResult.data)}
        onComplete={firstReportScreen.onComplete}
      />
    );
  }

  if (queryResult.isLoading || !queryResult.data) {
    return <InlineLoading />;
  }

  if (!viewModel) {
    return (
      <ErrorStateContainer>
        <p>Court opinion report data is unavailable.</p>
      </ErrorStateContainer>
    );
  }

  const { cluster, opinions, docket, court, judge } = viewModel;

  const tabs = [
    {
      label: 'Opinion',
      content: <OpinionsList clusterOpinions={opinions} />,
    },
    {
      label: `Similar Cases (${
        similarCasesQuery.isLoading ? '...' : similarCasesQuery.data?.count || 0
      })`,
      content: (
        <SimilarCases
          similarCases={similarCasesQuery.data || null}
          caseName={caseName}
          error={
            similarCasesQuery.isError
              ? 'Failed to load similar cases'
              : undefined
          }
          isLoading={similarCasesQuery.isLoading}
        />
      ),
    },
    ...(judge
      ? [
          {
            label: 'Judge',
            content: (
              <JudgeDetails
                judgeId={String(judge.person.id)}
                person={judge.person}
                positions={judge.positions}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <Box>
      <OpinionDetailsHeader
        selectedCase={cluster}
        embeddedDocket={docket}
        embeddedCourt={court}
      />
      <StyledTabsNavigation
        externalActiveTab={activeTab}
        onTabChanged={onTabChanged}
        tab={StyledTab}
        tabs={tabs}
      />
      <CourtCaseDisclaimer />
    </Box>
  );
}
